/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import type { Partner_Partner as EmbeddedPartner } from '__generated__/graphql-types';

import { Button, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { InformativeIcon } from '@coursera/cds-icons';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import EmbeddedContentPartner from 'bundles/unified-description-page-common/components/shared/EmbeddedContentPartner';
import Partner from 'bundles/unified-description-page-common/components/shared/Partner';
import PartnerSquareLogo from 'bundles/unified-description-page-common/components/shared/PartnerSquareLogo';
import Rating from 'bundles/unified-description-page-common/components/shared/Rating';
import InstructorItem from 'bundles/unified-description-page-common/components/shared/instructors/InstructorItem';
import EmbeddedPartnersModal from 'bundles/unified-description-page-common/components/shared/modals/EmbeddedPartnersModal';
import InstructorsModal from 'bundles/unified-description-page-common/components/shared/modals/InstructorsModal';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type { InstructorFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import type { PartnerFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import getRelatedPartnerName from 'bundles/unified-description-page-common/utils/getRelatedPartnerName';

import _t from 'i18n!nls/xdp';

const styles = {
  subsection: css`
    padding: var(--cds-spacing-300) 0;

    & + & {
      border-top: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200) 0;
    }
  `,
  subsectionHeader: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  ratings: css`
    display: flex;
    gap: var(--cds-spacing-200);
    align-items: center;
  `,
  ratingsLabel: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  linkButton: css`
    padding: 0;
    margin-left: 0;
    height: unset;
    width: unset;

    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
  item: css`
    & + & {
      padding-top: var(--cds-spacing-300);
    }
  `,
  viewAllButton: css`
    margin-top: var(--cds-spacing-300);
  `,
  industryPartners: css`
    display: flex;
    gap: var(--cds-spacing-200);
    flex-wrap: wrap;
  `,
};

type Props = {
  averageInstructorRating?: number | null;
  instructorRatingCount?: number | null;
  instructors?: InstructorFragment[] | null;
  numberOfInstructorsToShow?: number;
  partners?: PartnerFragment[] | null;
  industryPartners?: string[] | null;
  trackingLocation?: SectionName;
  disableCTA?: boolean;
  embeddedContentPartners?: EmbeddedPartner[] | null;
  numberOfEmbeddedContentPartnersToShow?: number;
};

const InstructorAndPartnerInfo = ({
  averageInstructorRating,
  instructorRatingCount,
  instructors,
  partners,
  industryPartners,
  numberOfInstructorsToShow = 3,
  trackingLocation,
  disableCTA,
  embeddedContentPartners,
  numberOfEmbeddedContentPartnersToShow = 3,
}: Props) => {
  const { isModalOpen, setModalOpen } = useModal();
  const { isModalOpen: isInstructorsModalOpen, setModalOpen: setInstructorsModalOpen } = useModal();
  const { isModalOpen: isEmbeddedContentPartnersModalOpen, setModalOpen: setEmbeddedContentPartnersModalOpen } =
    useModal();
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  const instructorsToShow =
    instructors &&
    instructors.length > 0 &&
    numberOfInstructorsToShow > 0 &&
    instructors.length > numberOfInstructorsToShow
      ? instructors.slice(0, numberOfInstructorsToShow)
      : instructors;

  const embeddedContentPartnersToShow =
    embeddedContentPartners &&
    numberOfEmbeddedContentPartnersToShow > 0 &&
    embeddedContentPartners.length > numberOfEmbeddedContentPartnersToShow
      ? embeddedContentPartners.slice(0, numberOfEmbeddedContentPartnersToShow)
      : embeddedContentPartners;

  const onClickViewAllInstructors = () => {
    track('click_button', {
      button: { name: 'description_page_instructor_more' },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
    setInstructorsModalOpen();
  };

  const onClickViewAllEmbeddedContentPartners = () => {
    track('click_button', {
      button: { name: 'description_page_embedded_partners_view_all' },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
    setEmbeddedContentPartnersModalOpen();
  };

  return (
    <React.Fragment>
      {instructors && instructors.length > 0 && (
        <div css={styles.subsection}>
          <div css={styles.subsectionHeader}>
            <Typography variant="h2semibold" component="h3">
              <FormattedMessage
                message={_t('{numberOfInstructors, plural, =1 {Instructor} other {Instructors}}')}
                numberOfInstructors={instructors.length}
              />
            </Typography>
            {Boolean(averageInstructorRating) && (
              <div css={styles.ratings}>
                <div css={styles.ratingsLabel}>
                  <Typography2 variant="bodySecondary" component="span">
                    {_t('Instructor ratings')}
                  </Typography2>
                  <Button
                    variant="ghost"
                    css={styles.linkButton}
                    onClick={setModalOpen}
                    aria-label={_t('Information about instructor ratings')}
                  >
                    <InformativeIcon size="small" />
                  </Button>
                  <Modal header={_t('Instructor ratings')} open={isModalOpen} setOpen={setModalOpen}>
                    <Typography2 component="p" variant="bodyPrimary">
                      {_t(
                        'We asked all learners to give feedback on our instructors based on the quality of their teaching style.'
                      )}
                    </Typography2>
                  </Modal>
                </div>
                <Rating rating={averageInstructorRating} ratingCount={instructorRatingCount} />
              </div>
            )}
          </div>
          {instructorsToShow && instructorsToShow.length > 0 && (
            <React.Fragment>
              {instructorsToShow.map((instructor) => (
                <div key={instructor.id} css={styles.item}>
                  <InstructorItem
                    instructor={instructor}
                    relatedPartnerName={getRelatedPartnerName(instructor.id, partners)}
                    disableCTA={disableCTA}
                    trackingLocation={trackingLocation}
                  />
                </div>
              ))}
              {instructors.length > instructorsToShow.length && (
                <React.Fragment>
                  <Button
                    variant="ghost"
                    edgeAlign="start"
                    size="small"
                    css={[styles.linkButton, styles.viewAllButton]}
                    onClick={onClickViewAllInstructors}
                    component={TrackedButton}
                    trackingName={trackingLocation ? `${trackingLocation}_instructors_more` : 'instructors_more'}
                    withVisibilityTracking={false}
                    requireFullyVisible={false}
                  >
                    {_t('View all #{numberOfInstructors} instructors', { numberOfInstructors: instructors.length })}
                  </Button>
                  <InstructorsModal
                    open={isInstructorsModalOpen}
                    setOpen={setInstructorsModalOpen}
                    instructors={instructors}
                    averageInstructorRating={averageInstructorRating}
                    instructorRatingCount={instructorRatingCount}
                    partners={partners}
                    disableCta={disableCTA}
                    width="large"
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>
      )}
      {partners && partners.length > 0 && (
        <div css={styles.subsection}>
          <div css={styles.subsectionHeader}>
            <Typography variant="h2semibold" component="h3">
              {_t('Offered by')}
            </Typography>
          </div>
          {partners.map((partner) => {
            return (
              <div key={partner.id} css={styles.item}>
                <Partner
                  partner={partner}
                  trackingName={trackingLocation ? `${trackingLocation}_partner_more` : 'partner_more'}
                  disableCTA={disableCTA}
                  trackingLocation={trackingLocation}
                />
              </div>
            );
          })}
        </div>
      )}
      {industryPartners && industryPartners.length > 0 && (
        <div css={styles.subsection}>
          <div css={styles.subsectionHeader}>
            <Typography variant="h2semibold" component="h3">
              {_t('Industry partners')}
            </Typography>
          </div>
          <div css={styles.industryPartners}>
            {industryPartners.map((partnerLogo, index) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <PartnerSquareLogo src={partnerLogo} name={_t('Partner #{index}', { index: index + 1 })} key={index} />
              );
            })}
          </div>
        </div>
      )}
      {embeddedContentPartnersToShow && embeddedContentPartnersToShow.length > 0 && (
        <div css={styles.subsection}>
          <div css={styles.subsectionHeader}>
            <Typography2 variant="subtitleMedium" component="h4">
              {_t('With content from')}
            </Typography2>
          </div>
          {embeddedContentPartnersToShow.map((partner) => (
            <EmbeddedContentPartner
              key={partner.id}
              partner={partner}
              disableCTA={disableCTA}
              trackingName={
                trackingLocation ? `${trackingLocation}_embedded_content_partner` : 'embedded_content_partner'
              }
              trackingLocation={trackingLocation}
            />
          ))}
          {embeddedContentPartners && embeddedContentPartners.length > embeddedContentPartnersToShow.length && (
            <React.Fragment>
              <Button
                variant="ghost"
                edgeAlign="start"
                size="small"
                css={[styles.linkButton, styles.viewAllButton]}
                onClick={onClickViewAllEmbeddedContentPartners}
                component={TrackedButton}
                trackingName={
                  trackingLocation
                    ? `${trackingLocation}_embedded_content_partners_view_all`
                    : 'embedded_content_partners_view_all'
                }
                withVisibilityTracking={false}
                requireFullyVisible={false}
              >
                {_t('View all #{numberOfPartners} educators', {
                  numberOfPartners: embeddedContentPartners.length,
                })}
              </Button>
              <EmbeddedPartnersModal
                open={isEmbeddedContentPartnersModalOpen}
                setOpen={setEmbeddedContentPartnersModalOpen}
                partners={embeddedContentPartners}
                disableCTA={disableCTA}
                trackingLocation={trackingLocation}
                width="large"
              />
            </React.Fragment>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default InstructorAndPartnerInfo;
